import Calendar from "model/calendar";
import { randomizeFromList } from "util/tools";
import { dialog, event } from "model/notification";
import clock from "util/clock";
import Debug from "debug";
const debug = Debug("action:walk");

const ActionType = "Walk";
const IN_TOWN = 12;
const DAY_ENABLED_STEP = 2;
const HEALTH_ENABLED_STEP = 5;
const THIRST_STEP = 7;
const DECISION_STEP = 8;
const DECISION_POINT_CHANCE = 1;

const seenIndices = new Set();
// These report the walker. Lines about the road itself belong in model/ambient, which the
// clock pushes whether or not the player walks — keep the two lists disjoint.
const messages = [
  `a few more steps`,
  `legs trudge on`,
  `a glimmer. town looms ahead`,
  `one foot and then the other`,
  `boots scuff the grit`,
];

const createNotification = (gain) => {
  const { steps, town } = gain;
  if (town) {
    return event(
      `the town`,
      "a forlorn outpost under the vast, unyielding sky.",
    );
  }

  // The town is already on screen by now. This is the step that closes the walk phase.
  if (steps > IN_TOWN) {
    return `past the first houses. no one on the street`;
  }

  switch (steps) {
    case 1:
      return `a shuffled step`;
    case DAY_ENABLED_STEP:
      return `the sun is punishing`;
    case 4:
      return dialog("where am I");
    case HEALTH_ENABLED_STEP:
      return `pounding headache.`;
    case 6:
      return dialog("blood on my rags ... mine?");
    case THIRST_STEP:
      return `gnawing thirst. pockets empty`;
    case 8:
      return dialog("who am I");
    case IN_TOWN:
      return `the town`;
    default:
      return randomizeFromList(messages, seenIndices);
  }
};

const attempt = (context) => {
  const { history } = context.party;
  const stepsWalked = history.attribs.stepsWalked.value + 1;

  const result = {
    success: false,
    gain: {
      steps: stepsWalked,
      city: false,
    },
    cost: {},
    eventTriggered: false,
  };

  if (stepsWalked === IN_TOWN) {
    result.gain.city = context.party.city;
  }
  if (stepsWalked > IN_TOWN) {
    result.success = true;
  }

  if (stepsWalked === DECISION_STEP) {
    // Randomly determine if a decision point should occur
    result.eventTriggered = Math.random() < DECISION_POINT_CHANCE;
  }

  debug(result);
  return result;
};

const executeSideEffects = (context, result) => {
  const { history, phases, resources } = context.party;
  const { steps } = result.gain;

  // Update steps walked in history
  history.delta({ stepsWalked: 1 }, context);
  // Once. Re-sending the delta re-fires onHistoryChanged and re-queues the story `enter`
  // event; handleArrive takes the Walk button away so it cannot happen in normal play, but
  // the guard is what makes that true rather than incidental.
  if (steps > IN_TOWN && !history.attribs.firstTown.value) {
    history.delta({ firstTown: true }, context);
  }
  // Add walk action to history
  history.add({ action: "WALK", result }, context);

  if (steps === DAY_ENABLED_STEP) {
    phases.enableCalendar(Calendar.PARTIAL);
  } else if (steps === HEALTH_ENABLED_STEP) {
    phases.enableResource(resources.attribs.health.index);
  } else if (steps === THIRST_STEP) {
    phases.enableResource(resources.attribs.water.index);
  }

  const notification = createNotification(result.gain);

  // The walker has just spoken. Push the road's next line back a full interval so the two
  // don't land together. Progress toward the phase is untouched — walking must not stall
  // the deadline.
  clock.resetAmbient();

  // Return any relevant information, like notifications
  return notification;
};

// Step IN_TOWN puts the town on screen. The press after it is the last of the walk phase —
// it fires ARRIVE, which advances the phase and takes the button away. Name it for what it
// does rather than leaving one more anonymous Walk while the player is already looking at
// the place. Action.js resolves a function title against gameState.
const title = ({ party }) =>
  party.history.attribs.stepsWalked.value >= IN_TOWN ? "Enter Town" : "Walk";

const walk = {
  title,
  type: ActionType,
  hotkey: "w",
  enabled: () => true,
  execute: (context) => {
    const outcome = attempt(context);
    const notification = executeSideEffects(context, outcome);

    const result = outcome.gain.city
      ? { location: outcome.gain.city }
      : undefined;
    return { result, outcome, notification };
  },
};

export { ActionType as WalkType, walk as default };
